import React, { useState, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import getName from "../util/getName";
import { AppContext } from "../util/AppContext";
import { ProductsModal, ProductsModalButton, isProductEnabledForCurrentTerritory, isProductEnabledForCurrentMarket } from "../components/ProductsModal";
import MessageTypes from "../util/messageTypes";
import { getConfigValue, isBatchPrintEnabledForProduct } from "../util/getConfigValue";
import { documentGeneratePlanless, documentGenerationType } from "../API/documentGenerate";
import AlertModal from '../components/AlertModal';
import { getBatchPrintMessages } from "../API/batchPrint";
import { useErrorBoundary } from "react-error-boundary";
import { focusTypeName, quickSheetTypeMessageId } from "../util/constants";
import { useRoutingValidation } from "../util/useRoutingValidation";
import { FavoriteDesignation, updateHcpAppState } from "../components/FavoriteDesignation";
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import CreateSalesAidStepProgressBar from "../components/CreateSalesAidStepProgressBar";
import { SalesAidStepIds, getPreviousRoute } from "../util/salesAidNavigation";
import { getPrescriberTypeLabel } from "../util/getHcpLabel";

function MessageSelectionPage() {
    const [appState, setAppState] = useContext(AppContext);
    const [showProductsModal, setShowProductsModal] = useState(false);
    const [productMessages, setProductMessages] = useState([]);
    const [currentProduct, setCurrentProduct] = useState();
    const [productImageSource, setProductImageSource] = useState();
    const { state } = useLocation();
    const [alert, setAlert] = useState(state?.alert || null);
    const [generatingMessageCode, setGeneratingMessageCode] = useState(null);
    const prescriberType = (appState?.prescriberType);
    const focusType = (appState?.focus);
    const [batchPrintMessages, setBatchPrintMessages] = useState([]);
    const { showBoundary } = useErrorBoundary();
    const { setBreadcrumbs } = useBreadcrumbs();
    const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';
    const navigate = useNavigate();

    const hcpNameRequired = getConfigValue(appState.config?.PrintOptions?.HCPName?.Required, appState)?.Value === 'true';
    const ehrBackPageRequired = getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Required, appState)?.Value === 'true';
    const hcpCoverPageRequired = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Required, appState)?.Value === 'true';
    const savingsCardRequired = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Required, appState)?.Value === 'true';
    const locationNameRequired = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Required, appState)?.Value === 'true';
    const salesRepInfoRequired = getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Required, appState)?.Value === 'true';
    const frmInfoRequired = getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Required, appState)?.Value === 'true';
    const printOptionsHcpNameEnabled = getConfigValue(appState.config?.PrintOptions?.HCPName?.Enabled, appState)?.Value === 'true';
    const printOptionsEhrBackPageEnabled = getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Enabled, appState)?.Value === 'true';
    const printOptionsCoverPageEnabled = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Enabled, appState)?.Value === 'true';
    const savingsCardIsEnabled = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Enabled, appState)?.Value === 'true';
    const printOptionsLocationNameEnabled = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Enabled, appState)?.Value === 'true';
    const printOptionsSelectSalesRepInfoEnabled = getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Enabled, appState)?.Value === 'true';
    const printOptionsSelectFRMInfoEnabled = getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Enabled, appState)?.Value === 'true';
    const hasOptionalPrintOptions =
        (printOptionsHcpNameEnabled && !hcpNameRequired) ||
        (printOptionsEhrBackPageEnabled && !ehrBackPageRequired) ||
        (printOptionsCoverPageEnabled && !hcpCoverPageRequired) ||
        (savingsCardIsEnabled && !savingsCardRequired) ||
        (printOptionsLocationNameEnabled && !locationNameRequired) ||
        (printOptionsSelectSalesRepInfoEnabled && !salesRepInfoRequired) ||
        (printOptionsSelectFRMInfoEnabled && !frmInfoRequired);
    useEffect(() => {
        setBreadcrumbs([
            { label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" },
        ]);
    }, [setBreadcrumbs]);

    useRoutingValidation();

    useEffect(() => {
		const getDefaultProduct = function () {
			for (let index = 0; index < appState?.sellSheetProductDetails.length; index++) {
				const clientProductId = appState?.sellSheetProductDetails[index].clientProductId;
				if (!isProductEnabledForCurrentMarket(appState, clientProductId)) {
					continue;
				}
				if (!isProductEnabledForCurrentTerritory(appState?.territory, clientProductId)) {
					continue;
				}
				const product = appState?.products?.find((p) => p.productId === clientProductId);
				if (product && product?.messages?.length > 0) {
					setCurrentProduct(appState?.sellSheetProductDetails[index]);
					setProductMessages(product.messages);
					setProductImageSource(appState?.sellSheetProductDetails[index].imagePath);
					setAppState({ ...appState, currentProduct: appState?.sellSheetProductDetails[index] });
					break;
				}
			}
		};
		if (
			appState?.currentProduct === '' ||
			!isProductEnabledForCurrentMarket(appState, appState.currentProduct.clientProductId) ||
			!isProductEnabledForCurrentTerritory(appState?.territory, appState.currentProduct.clientProductId) ||
			!appState?.products?.find((p) => p.productId === appState.currentProduct?.clientProductId)
		) {
			getDefaultProduct();
		} else {
			setCurrentProduct(appState.currentProduct);
			setProductMessages(appState?.products?.find((i) => i.productId === appState.currentProduct?.clientProductId)?.messages);
			setProductImageSource(appState.currentProduct?.imagePath);
		}
	}, [appState, appState.currentProduct, appState?.sellSheetProductDetails, setAppState]);

    useEffect(() => {
        if ((focusType === focusTypeName.HCPs && (!productMessages || productMessages.length === 0)) || (focusType === focusTypeName.Messages && (!batchPrintMessages || batchPrintMessages.length === 0))) {
            setAlert({ type: 'primary', heading: (getConfigValue(appState?.config?.MessageSelectionPage?.InfoMessage?.Title, appState)?.Value || null), message: (getConfigValue(appState?.config?.MessageSelectionPage?.InfoMessage?.Body, appState)?.Value || null) });
        } else {
            setAlert()
        }
    }, [appState, productMessages, batchPrintMessages])

    useEffect(() => {
        const fetchBatchPrintMessages = async (product) => {
            setCurrentProduct(product);
            setProductImageSource(product.imagePath);
            const result = await getBatchPrintMessages(appState?.territory?.id, product.clientProductId);
            const categories = result.map(m => ({ category: m.name, messageCodes: m.messageCodes, messageTypeId: m.messageTypeId, messageType: m.messageType }));
            setAppState({ ...appState, currentProduct: product, batchPrintCategories: categories });
            setBatchPrintMessages(categories);
        };

        // Market mapping is a hard constraint (the product isn't sold in this market at all), but
        // territory assignment is a softer preference - fall back to a market-valid product rather
        // than showing nothing, mirroring hcpPage.js's product resolution.
        const getDefaultProductBatchPrint = async () => {
            const products = appState?.sellSheetProductDetails || [];
            const marketProducts = products.filter(p => p.clientProductId && isBatchPrintEnabledForProduct(appState, p.clientProductId) && isProductEnabledForCurrentMarket(appState, p.clientProductId));
            const availableProducts = marketProducts.filter(p => isProductEnabledForCurrentTerritory(appState?.territory, p.clientProductId));
            const product = availableProducts[0] || marketProducts[0];
            if (product) {
                await fetchBatchPrintMessages(product);
            }
        };

        if (focusType === focusTypeName.Messages) {
            const currentProductId = appState?.currentProduct?.clientProductId;
            const isCurrentProductValid = appState?.currentProduct !== "" && currentProductId != null
                && isBatchPrintEnabledForProduct(appState, currentProductId)
                && isProductEnabledForCurrentMarket(appState, currentProductId);

            if (isCurrentProductValid) {
                fetchBatchPrintMessages(appState.currentProduct).catch(error => { showBoundary(error) });
            } else {
                getDefaultProductBatchPrint().catch(error => { showBoundary(error) });
            }
        }
    }, [appState.territory?.id, appState.currentProduct, appState?.sellSheetProductDetails, appState?.marketProductMap, setAppState])

    function handleCategoryButtonClick(category) {
        setAppState({ ...appState, category: category, isQuickSheetMessageType: category.messageTypeId === quickSheetTypeMessageId});
    }

    async function handleMessageButtonClick(message) {
        handleCategoryButtonClick(message);
        if (message.messageType !== MessageTypes.ImprovedAccess && !message.isContainerMessage && message.hasPlanTable === false && !hasOptionalPrintOptions) {
            setGeneratingMessageCode(message.messageCode);
            try {
                const props = {
                    productId: appState.currentProduct.clientProductId,
                    prescriberId: appState.prescriber.id,
                    messageType: message.messageCode,
                    selectedPlans: [],
                    printPrescriberName: printOptionsHcpNameEnabled && hcpNameRequired,
                    addEHRBackPage: printOptionsEhrBackPageEnabled && ehrBackPageRequired,
                    marketid: Number(appState.market?.id),
                    territoryid: appState.territory?.id,
                    generateFor: documentGenerationType.Generate,
                    columnKeys: [],
                    messageTypeId: message.messageTypeId,
                    improvedAccessPlan: null,
                    includeLocationName: printOptionsLocationNameEnabled && locationNameRequired,
                    includeSavingsCard: savingsCardIsEnabled && savingsCardRequired,
                    addCoverPage: printOptionsCoverPageEnabled && hcpCoverPageRequired,
                    targetLevel: prescriberType,
                    includeSalesRep: printOptionsSelectSalesRepInfoEnabled && salesRepInfoRequired,
                    includeFRM: printOptionsSelectFRMInfoEnabled && frmInfoRequired,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    purchaseOrderNumber: appState.purchaseOrderNumber
                };
                const response = await documentGeneratePlanless(props);
                setAppState({
                    ...appState,
                    lastGeneratedDocument: props,
                    sellSheetId: response.sellSheetId,
                    purchaseOrderNumber: response.purchaseOrderNumber,
                });
                const url = URL.createObjectURL(response.blob);
                navigate('/GenerateDocumentPage', {
                    state: { pdfURL: url, fileName: response.fileName },
                });
            } catch (err) {
                console.error(err);
                setAlert({ type: 'danger', message: getConfigValue(appState.config?.GenerateDocument?.Error?.Text, appState)?.Value });
            } finally {
                setGeneratingMessageCode(null);
            }
            return;
        }
        navigate(handleNavigation(message));
    }

    function onProductClick(product) {
        setAppState({ ...appState, currentProduct: product });
        setCurrentProduct(product);
        setShowProductsModal(false);
        setProductMessages(appState?.products?.find(i => i.productId === product.clientProductId)?.messages)
        setProductImageSource(product.imagePath)
    }

    function handleNavigation(message) {
        const isContainerMessage = message.isContainerMessage;
        if (message.messageType === MessageTypes.ImprovedAccess) {
            return '/WinPlanListPage';
        }
        if (isContainerMessage) {
            return '/ContainerMessagePage';
        }
        if (message.messageTypeId === quickSheetTypeMessageId) {
            return '/QuickSheetsPage'
        }

        return '/PlanListPage';
    }

    return (
        <div>
            <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.CONFIGUREMESSAGE} focusType={focusType} />

            <div className="container-xl mt-3 mt-md-4" style={useStickyNavigation ? { marginBottom: '2rem' } : {}}>
                <div className="row d-flex align-items-md-center mb-md-5">
                    <div className="col-12 col-md-6 order-2 order-md-1">
                        <h1 className="type-20 color-grey-d1 mt-4 mt-md-0">
                            {getConfigValue(appState.config?.MessageSelectionPage?.CallToAction, appState)?.Value}
                        </h1>
                    </div>
                    <div className="col-12 col-md-6 order-1 order-md-2">
                        {focusType === focusTypeName.HCPs ?
                            <div className="row justify-content-end">
                                <div className="col-6 col-md-auto type-14 text-md-end favorite-hcp">
                                    <div className="color-grey-d1">
                                        {prescriberType && getPrescriberTypeLabel("MessageSelectionPage", prescriberType, appState)}
                                    </div>
                                    {appState.prescriber?.id && <FavoriteDesignation hcp={appState.prescriber} updateCallback={() => updateHcpAppState(appState.prescriber, setAppState)} ></FavoriteDesignation>}{getName(appState)}
                                </div>
                                <div className="col-6 col-md-auto type-14 text-md-end">
                                    <div className="color-grey-d1">
                                        {getConfigValue(appState.config?.MessageSelectionPage?.TerritoryLabel, appState)?.Value}
                                    </div>
                                    {appState.territory?.name}
                                </div>
                            </div>
                            : ""}
                    </div>
                </div>

                <div className="row">
                    {/* This section will be replaced with the config data to pull in the brands */}
                    <div className="col-12 col-md-4 col-lg-3">
                        <ProductsModalButton
                            setShowProductsModal={setShowProductsModal}
                            productImageSource={productImageSource}
                            productName={currentProduct?.name}
                        ></ProductsModalButton>
                    </div>
                    {alert && (
                        <div className="col-12 col-md-8 col-lg-9">
                            <AlertModal alert={alert} />
                        </div>
                    )}
                    {/* This section will be replaced with the config data to pull in the brands */}
                    <div className="col-12 col-md-8 col-lg-9">
                        {focusType === focusTypeName.HCPs && productMessages?.length > 0 &&
                            productMessages.toSorted((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder).map((message, index) => {
                                return (
                                    <React.Fragment key={message.messageCode}>
                                        {index > 0 && (
                                            <hr className="my-5" />
                                        )}

                                        <div className="row">
                                            <div className="col-12 col-lg-8">
                                                <h2 className="type-20 fw-bold">
                                                    {message.category}
                                                </h2>
                                                <div
                                                    className="my-md-0"
                                                    dangerouslySetInnerHTML={{
                                                        __html: message.message,
                                                    }}>
                                                </div>
                                            </div>
                                            <div className="col-12 col-lg-4 d-lg-flex flex-column justify-content-lg-center">
                                                {/* Link them to Improved access or Container Messages*/}
                                                <button
                                                    type="button"
                                                    data-messagecode={message.messageCode}
                                                    onClick={() => handleMessageButtonClick(message)}
                                                    disabled={generatingMessageCode === message.messageCode}
                                                    className={`d-inline-flex btn btn-outline-primary btn-icon mt-2${generatingMessageCode === message.messageCode ? ' btn-working' : ''}`}
                                                >
                                                    {message.category}{" "}
                                                    <span className="fa-regular fa-chevron-right btn-icon"></span>
                                                </button>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        {focusType === focusTypeName.Messages && batchPrintMessages?.length > 0 &&
                            batchPrintMessages?.toSorted((a, b) => a.categoryDisplayOrder - b.categoryDisplayOrder).map((message, index) => {
                                return (
                                    <React.Fragment key={message.messageTypeId}>
                                        {index > 0 && (
                                            <hr className="my-5" />
                                        )}
                                        <div className="row">
                                            <div className="col-12 col-lg-8">
                                                <h2 className="type-20 fw-bold">
                                                    {message.category}
                                                </h2>
                                            </div>
                                            <div className="col-12 col-lg-4 d-lg-flex flex-column justify-content-lg-center">
                                                {/* Link them to Improved access */}
                                                <Link
                                                    to={'/SelectTargetBatchPage'}
                                                    onClick={() => handleCategoryButtonClick(message)}
                                                    className="d-inline-flex btn btn-outline-primary btn-icon mt-2"
                                                >
                                                    {message.category}{" "}
                                                    <span className="fa-regular fa-chevron-right btn-icon"></span>
                                                </Link>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                );
                            })
                        }

                        <div className="my-7"></div>
                    </div>
                </div>
            </div>

            <div className={`${useStickyNavigation ? 'button-bottom-container-sticky-low-z' : ''}`}>
                <div className="container-xl">
                    <div className="row justify-content-center justify-content-md-end py-3">
                        <div className="col-12 col-md-8 col-lg-9">
                        </div>
                    </div>
                    <div className="row justify-content-center justify-content-md-end mb-5">
                        <div className="col-10 col-sm-8 col-md-4 col-lg-3">
                            <Link
                                to={getPreviousRoute(SalesAidStepIds.CONFIGUREMESSAGE, appState, focusType)}
                                className="btn btn-secondary btn-flex flex-fill w-100">
                                {getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
                            </Link>
                        </div>
                    </div>
                </div> 
            </div> 
            
            <ProductsModal
                show={showProductsModal}
                setShow={setShowProductsModal}
                onProductClick={onProductClick}
                onlySellSheetProducts={true}
            />
        </div>
    );
}

export default MessageSelectionPage;
